import {
  Collection, Layout, locale, util
} from '@glu/core';
import FlexDropdown from '@glu/flex-dropdown';
import NumberRangeSearch from './numberRangeSearch';
import AmountSearch from './amountSearch';
import DateRangeSearch from './dateRangeSearch';
import EnumSearch from './enumSearch';
import FieldFuzzySearch from './fieldFuzzySearch';
import GridFuzzySearch from './gridFuzzySearch';
import ManageFiltersLink from './manageFiltersLink';
import MultiSelectSearch from './multiSelectSearch';
import NumericSearch from './numericSearch';

import template from './fieldTypeSearch.hbs';

function filterableColumns(column) {
  return !(column.has('filterable') && column.get('filterable') === false);
}

function getHiddenColumns(column) {
  const condition = column.get('condition');
  const isFunction = util.isFunction(condition) && condition(column) === false;
  const isBoolean = util.isBoolean(condition) && !condition;
  return (isFunction || isBoolean);
}

function getVisibleColumn(column) {
  return !getHiddenColumns(column);
}

export default Layout.extend({
  template,
  className() {
    return 'grid-filter-selection clearfix';
  },

  events: {
    'click @ui.$removeBtn': 'handleRemoveFilter'
  },

  regions: {
    searchContainer: '[data-control=searchContainer]',
    select: '.region-grid-select-filter'
  },

  collectionEvents: {
    'reset remove add change:condition': 'hideSearchView render'
  },

  ui: {
    $removeBtn: 'button[data-control="removeFilter"]'
  },

  initialize() {
    this.filtersCollection = this.options.filtersCollection;
    if (this.options.model) {
      this.searchView = this.getSearchViewByType(this.options.model.get('type'), this.options.model);
    }

    this.first = this.options.first || false;

    if (this.options.masterCollection) {
      this.listenTo(this.options.masterCollection, 'grid:externalFilter', () => this.hideSearchView());

      this.collection.each(column => {
        const field = column.get('field');
        if (field) {
          this.listenTo(this.options.masterCollection, `grid:filter:${field}`, value => {
            this.filterBy(value, field);
          });
          this.listenTo(this.options.masterCollection, `grid:removeFilter:${field}`, value => {
            this.removeFilter(value, field);
          });
        }
      });
    }

    if (!this.filtersCollection) {
      return;
    }

    this.listenTo(this.filtersCollection, 'grid:reopen:filter', this.reopenFilter);
  },

  removeFilter(value, field) {
    const filters = this.options.filterProc.filters.where({
      field,
      value
    });

    if (util.isNullOrUndefined(value) || (filters && filters.length === 1)) {
      this.options.filterProc.removeFilters(field);
    }
  },

  filterBy(value, field) {
    this.handleSelectType([field]);
    this.searchView.filterBy(field, value);
  },

  createDropdown() {
    const { grid } = this.options;

    const flexDropdownOptions = {
      data: this.getDropDownData(),
      filter: false,
      multiSelect: false,
      showGroups: true,
      preSelectedIds: this.model && this.model.get('field') ? [this.model.get('field')] : [],
      defaultSelectMessage: locale.get('selectFields'),
      label: locale.get('filter'),
      showTooltip: false,
      showCancelIcon: false,
      clearBtn: false,
      disableMultiButton: true,
      dataQA: `${grid.options.id}-grid-filter-dropdown`
    };

    if (this.options.disableAuxControl) {
      util.extend(flexDropdownOptions, {
        customView: ManageFiltersLink,
        customViewOptions: {
          grid: this.options.grid,
          collection: this.filtersCollection,
          filterViewClass: this.options.filterViewClass,
          columns: this.collection,
          filterProc: this.options.filterProc
        }
      });
    }

    const dropdown = new FlexDropdown(flexDropdownOptions);

    this.listenTo(dropdown, 'selectionChanged:id', this.handleSelectType);

    return dropdown;
  },

  getDropDownData() {
    const itemsCollection = new Collection(this.collection.filter(filterableColumns));
    const visibleColumns = itemsCollection.filter(getVisibleColumn);
    const hiddenColumns = itemsCollection.filter(getHiddenColumns);
    const data = [];

    const mapper = item => {
      const id = item.get('field') || '';

      return {
        id,
        name: item.get('title') || id
      };
    };

    if (this.options.gridSearch) {
      data.unshift({
        id: 'table',
        name: locale.get('entireTable')
      });
    }

    data.push({
      id: 'visibleColumns',
      name: '',
      child: util.map(visibleColumns, mapper)
    });

    if (!hiddenColumns.length) {
      return data;
    }

    data.push({
      id: 'hiddenColumns',
      name: locale.get('notCurrentlyDisplayed'),
      child: util.map(hiddenColumns, mapper)
    });

    return data;
  },

  reopenFilter(filter) {
    if (!this.dropdown) {
      return;
    }

    this.dropdown.setSelectedIds(filter.get('field'));
  },

  /**
   * React to changes in the filter dropdown.
   *
   * @param ids type Array
   * @returns {*}
   */
  handleSelectType(ids) {
    const field = ids[0];

    if (!field) {
      // user selected the default value (ie, 'Select fields') or click clear button.
      this.hideSearchView();
    } else if (field === 'table') {
      // user selected the 'entire table' option.
      this.selectSearchByType('table', {});
    } else {
      const model = this.getModelByField(field);
      const { grid } = this.options;

      this.selectSearchByType(model.get('type'), model, grid);
    }

    return field;
  },

  getModelByField(field) {
    const model = this.collection.find((column) => field === column.get('field'));

    const filterModel = this.filtersCollection.find((filter) => field === filter.get('field'));

    if (!filterModel) {
      return model.clone();
    }

    // need to merge filter model with columns model
    // as columns model has additional filter parameters
    filterModel.set(util.extend(model.toJSON(), filterModel.toJSON()), {
      // to avoid re-rendering
      silent: true
    });

    return filterModel.clone();
  },

  selectSearchByType(type, model, grid) {
    type = type || 'string';
    const view = this.getSearchViewByType(type, model, grid);
    this.setSearchView(view);
    this.searchContainer.show(view);
  },

  setSearchView(view) {
    if (this.searchView) {
      this.$(this.searchContainer.el).removeClass(this.searchView.className);
    }

    this.searchView = view;
    this.$(this.searchContainer.el).addClass(this.searchView.className);

    this.listenTo(this.searchView, 'filter', this.handleSearchViewFilter);
  },

  disable() {
    this.disabled = true;
    this.$('.flex-dropdown .selection').addClass('disabled');
    if (this.searchView) {
      this.searchView.disable();
    }
  },

  enable() {
    this.disabled = false;
    this.$('.flex-dropdown .selection').removeClass('disabled');
    if (this.searchView) {
      this.searchView.enable();
    }
  },

  toggle() {
    return this.isDisabled() ? this.enable() : this.disable();
  },

  isDisabled() {
    return this.disabled === true;
  },

  handleRemoveFilter() {
    this.trigger('filter:remove', this);
  },

  handleSearchViewFilter(filter) {
    this.trigger('filter', filter);
  },

  onBeforeRender() {
    this.initializeDropdown();
  },

  onRender() {
    if (this.searchView && this.searchView.filterValue === undefined) {
      this.searchContainer.show(this.searchView);
    }

    this.showDropdown();
  },

  initializeDropdown() {
    if (this.dropdown) {
      this.dropdown.close();
    }
    this.dropdown = this.createDropdown();
  },

  showDropdown() {
    this.select.show(this.dropdown);
  },

  getSearchViewByType(type, model, grid) {
    let view;

    switch (type) {
      case 'table':
        view = new GridFuzzySearch();
        break;
      case 'date':
        view = new DateRangeSearch({
          filterBadgeView: grid.filterBadgeView,
          model,
          dateFormat: model.get('dateFormat')
        });
        view.setField(model.get('field'));
        break;
      case 'enum':
        view = new EnumSearch({
          model
        });
        break;
      case 'number':
        view = new NumericSearch({
          model
        });
        break;
      case 'numberRange':
        view = new NumberRangeSearch({
          model
        });
        break;
      case 'amount':
        view = new AmountSearch({
          model
        });
        break;
      case 'multiselect':
        view = new MultiSelectSearch({
          model,
          grid
        });
        break;
      case 'string':
      default:
        view = new FieldFuzzySearch({
          model,
          collection: this.collection
        });
        break;
    }

    this.listenTo(view, {
      filter: this.hideSearchView,
      'filter:clear': this.clearFilter,
      'filter:remove': this.handleRemoveFilter
    });

    return view;
  },

  /**
   * Remove the existing search view (if present), leaving only the filter dropdown visible.
   */
  hideSearchView() {
    if (this.isClosed || !this.searchView || this.options.advancedSearch) {
      return;
    }

    this.searchView.close();
    this.searchView = null;

    if (this.dropdown) {
      this.dropdown.setSelectedIds('');
    }
  },

  clearFilter(view) {
    const field = view.model.get('field');

    if (this.options.masterCollection) {
      this.options.masterCollection.trigger(`grid:clear:filter:${field}`, this);
    }

    this.options.filterProc.removeFilters(field);
    this.hideSearchView();
  },

  onClose() {
    if (!this.dropdown) {
      return;
    }

    this.dropdown.close();
  }
});
